import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from } from '@apollo/client'
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename'
import { fromJS, List, Map, Set } from 'immutable'
import pick from 'lodash/pick'
import { bindActionCreators } from 'redux'
import { connect as connectRedux, useSelector } from 'react-redux'
import { compose } from 'recompose'
import {
  getViewRecordsAsList,
  getRecords,
  getViewProcessing,
  filterRecords,
  getViewError,
} from 'services/data'
import { createAuthorization } from 'services/auth'
import { selectConfig } from 'services/config/redux/selectors'
import { selectAuth } from 'services/auth/redux/selectors'
import {
  getDataList as getDataListAction,
  setDataApplicationByKey as setDataApplicationByKeyAction,
} from 'services/data/redux/actions'
import {
  CONTENT_DETAIL_APPLICATION_KEY,
  CONTENT_STORE_KEY,
  createContentDetailViewOptions,
  createContentMediaOptions,
  createContentTranslationsViewOptions,
} from 'services/content'
import {
  CONTENT_TYPE_FAQ,
  CONTENT_TYPE_GUIDE,
  CONTENT_TYPE_GUIDE_DAY,
  CONTENT_TYPE_SERIES,
  CONTENT_TYPE_VIDEO,
  CONTENT_TYPE_TAG,
  CONTENT_TYPE_PERSON,
  CONTENT_TYPE_PROMOTION,
  CONTENT_TYPE_PRODUCT,
  CONTENT_TYPE_BONUS_PACKAGE,
  CONTENT_TYPE_SERVICE,
  CONTENT_TYPE_ARTICLE,
  CONTENT_TYPE_COURSE,
  CONTENT_TYPE_COURSE_MODULE,
} from 'services/content/content-types'
import { MEDIA_INFO_STORE_KEY, MEDIA_STORE_KEY } from 'services/media'
import VideoDetail from './video-detail'
import CourseDetail from './course-detail'
import CourseModuleDetail from './course-module-detail'
import { getCurrentContentDetail } from './helpers'
import { ContentDetailLoader } from './content-detail-loader'
import './index.scss'

/**
 * Get the component state by combining all redux state
 * @param {Object} props The props
 * @param {Map} props.data The redux data state
 * @param {Map} props.location The redux location state
 * @returns The entire component state using props and combined redux state
 */
function getState (props) {
  const { data, location, storeKey } = props
  const content = getCurrentContentDetail(props)
  const options = createContentDetailViewOptions({ location })
  const contentRecord =
    getViewRecordsAsList({
      data,
      storeKey,
      options,
    }).first() || Map()
  const processing = getViewProcessing({
    data,
    storeKey,
    options,
  })
  const viewError = getViewError({
    data,
    storeKey,
    options,
  })
  const errors = viewError ? fromJS(viewError.response.body.errors) : undefined
  const mediaOptions = createContentMediaOptions({ content })
  const processingMedia = getViewProcessing({
    data,
    storeKey: MEDIA_STORE_KEY,
    options: mediaOptions,
  })
  const mediaRecords = getViewRecordsAsList({
    data,
    storeKey: MEDIA_STORE_KEY,
    options: mediaOptions,
  })
  const processingTranslations = getViewProcessing({
    storeKey,
    data,
    options: createContentTranslationsViewOptions(content),
  })
  const mediaInfoRecords =
    getRecords({
      data,
      storeKey: MEDIA_INFO_STORE_KEY,
    }) || Map()
  const references = content.get('references') || List()
  const seriesRef = references.find((r) => r.get('type') === 'series')
  const contentSeries =
    (seriesRef &&
      filterRecords(
        { data, storeKey },
        (v) =>
          v.get('contentId') === seriesRef.get('contentId') &&
          v.get('contentType') === seriesRef.get('contentType'),
      ).first()) ||
    Map()
  return {
    errors,
    processing,
    processingMedia,
    processingTranslations,
    content,
    contentSeries,
    contentRecord,
    mediaList: Set([
      Map({ label: 'Preview', media: Map(), name: 'previewMediaId' }),
      Map({ label: 'Feature', media: Map(), name: 'featureMediaId' }),
    ]).map((v) => {
      const mediaId = content.get(v.get('name'))
      let media = mediaRecords.find((m) => m.get('mediaId') === mediaId) || Map()
      media = media.set('mediaInfo', mediaInfoRecords.get(mediaId) || Map())
      return v.set('media', media)
    }),
  }
}

/**
 * Component to display and edit content detail
 * @param {Object} props The props
 * @param {String} props.storeKey The store key
 * @param {String} props.applicationKey The application key
 * @param {Map} props.data The data state
 * @param {Map} props.location the location state
 * @param {typeof setDataApplicationByKeyAction} props.setDataApplicationByKey
 *   Dispatch a set data application by key action
 */
function ContentDetail (props) {
  const { content, contentRecord, processing, processingMedia, processingTranslations } =
    getState(props)

  const auth = useSelector(selectAuth)
  const config = useSelector(selectConfig)
  const server = config.getIn(['data', 'servers', 'graphql'])

  const removeTypenameLink = removeTypenameFromVariables()

  const httpLink = new HttpLink({
    uri: `${server}/graphql`,
    headers: {
      authorization: createAuthorization(auth),
    },
  })

  const client = new ApolloClient({
    link: from([removeTypenameLink, httpLink]),
    cache: new InMemoryCache(),
  })

  if (content.isEmpty() && (processing || processingMedia || processingTranslations)) {
    return <ContentDetailLoader />
  }
  if (contentRecord.isEmpty()) {
    return <div>The content was not found</div>
  }
  switch (content.get('contentType')) {
    case CONTENT_TYPE_FAQ:
    case CONTENT_TYPE_PRODUCT:
    case CONTENT_TYPE_BONUS_PACKAGE:
    case CONTENT_TYPE_GUIDE:
    case CONTENT_TYPE_GUIDE_DAY:
    case CONTENT_TYPE_PERSON:
    case CONTENT_TYPE_PROMOTION:
    case CONTENT_TYPE_SERIES:
    case CONTENT_TYPE_SERVICE:
    case CONTENT_TYPE_TAG:
    case CONTENT_TYPE_VIDEO:
    case CONTENT_TYPE_ARTICLE:
      return (
        <ApolloProvider client={client}>
          <VideoDetail />
        </ApolloProvider>
      )
    case CONTENT_TYPE_COURSE:
      return (
        <ApolloProvider client={client}>
          <CourseDetail />
        </ApolloProvider>
      )
    case CONTENT_TYPE_COURSE_MODULE:
      return (
        <ApolloProvider client={client}>
          <CourseModuleDetail />
        </ApolloProvider>
      )
    default:
      return <div>Content type {content.get('contentType')} is not set up to be edited</div>
  }
}

ContentDetail.propTypes = {
  applicationKey: PropTypes.string.isRequired,
  data: ImmutablePropTypes.map.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  location: ImmutablePropTypes.map.isRequired,
  setDataApplicationByKey: PropTypes.func.isRequired,
  storeKey: PropTypes.string.isRequired,
}

ContentDetail.defaultProps = {
  storeKey: CONTENT_STORE_KEY,
  applicationKey: CONTENT_DETAIL_APPLICATION_KEY,
}

export default compose(
  connectRedux(
    (state) => pick(state, ['data', 'location']),
    (dispatch) =>
      bindActionCreators(
        {
          getDataList: getDataListAction,
          setDataApplicationByKey: setDataApplicationByKeyAction,
        },
        dispatch,
      ),
  ),
)(ContentDetail)
